import React, { useState, useEffect, useCallback } from 'react';
import {
  Star, MessageSquare, BookOpen, Users, BarChart3, ChevronDown, ChevronUp,
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import {
  PageHeader, AppCard, AppCardContent,
  EmptyState, AppBadge,
} from '../ui';
import DataTable from '../Common/DataTable';
import CommonToaster from '../Common/CommonToaster';

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); AdminFeedbackPage.scss deleted
// with it — and roughly a third of that sheet was already dead. `.afb-table`,
// `.afb-row`, `.afb-pagination`, `.afb-pagination__info`, `.afb-pagination__btns`,
// `.afb-page-btn` and `.afb-page-num` (about 90 lines, thead/tbody/hover rules
// and a whole pager) are leftovers from before this page moved to
// `Common/DataTable`, which draws its own table and its own pager. Nothing has
// rendered any of them for some time.
//
// The filled star was `#f59e0b`, which is `--warning` to the byte. Its focus
// glow was `rgba(59, 130, 246, 0.1)` — a BLUE ring under a `--primary` border,
// so a focused filter drew two different colours at once.
//
// The empty star reads `--border-primary` deliberately: it is the outline of a
// thing that is not there, so it tracks the hairline colour, not an ink.
const STAT_HEADER =
  'tw-mb-3 tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-content ' +
  '[&_svg]:tw-text-content-muted';

const FILTER_SELECT =
  'tw-min-w-[160px] tw-max-w-[200px] tw-truncate tw-rounded-sm tw-border tw-border-[var(--input-border)] ' +
  'tw-bg-[var(--input-bg)] tw-px-3 tw-py-[0.4375rem] tw-text-[0.8125rem] tw-text-content ' +
  'focus:tw-border-primary focus:tw-outline-none focus:tw-ring-[3px] focus:tw-ring-primary/10';

const DETAIL_HEADING =
  'tw-mb-2 tw-flex tw-items-center tw-gap-1.5 tw-text-[0.8125rem] tw-font-semibold ' +
  'tw-text-content [&_svg]:tw-text-content-muted';

const DETAIL_ITEM =
  'tw-flex tw-items-center tw-justify-between tw-py-1 tw-text-[0.8125rem] tw-text-content-muted ' +
  '[&>span:first-child]:tw-text-content';

const SUGGESTIONS =
  'tw-m-0 tw-whitespace-pre-wrap tw-rounded-sm tw-border tw-border-line-soft tw-bg-surface-raised ' +
  'tw-px-3 tw-py-2.5 tw-text-[0.8125rem] tw-text-content';

/* ─── Avg Star Display ─── */
function AvgStars({ value, label }) {
  const v = value ? parseFloat(value).toFixed(1) : '—';
  const filled = Math.round(value || 0);
  return (
    <div className="tw-flex tw-flex-col tw-gap-[0.125rem]">
      <div className="tw-flex tw-items-center tw-gap-[0.125rem]">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star key={s} size={14} className={s <= filled ? 'tw-text-warning' : 'tw-text-[var(--border-primary)]'} />
        ))}
        <span className="tw-ms-1.5 tw-text-[0.8125rem] tw-font-medium tw-text-content">{v}</span>
      </div>
      <span className="tw-text-xs tw-text-content-muted">{label}</span>
    </div>
  );
}

/* ─── Feedback rating helpers ─── */
const miniStars = (val) => {
  if (!val) return <span className="tw-text-[0.8125rem] tw-text-content-subtle">—</span>;
  return (
    <span className="tw-inline-flex tw-gap-px">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} size={12} className={s <= val ? 'tw-text-warning' : 'tw-text-[var(--border-primary)]'} />
      ))}
    </span>
  );
};

const courseAverage = (fb) =>
  (((fb.courseContent || 0) + (fb.coursePractice || 0) + (fb.courseRelevance || 0) + (fb.courseDifficulty || 0)) / 4).toFixed(1);

const trainerAverage = (fb) => {
  const vals = [fb.trainerTeachingQuality, fb.trainerClarity, fb.trainerDoubtHandling, fb.trainerEngagement].filter(Boolean);
  if (!vals.length) return null;
  return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
};

/* ─── Expanded detail content (rendered via DataTable.renderExpandedRow) ─── */
function FeedbackDetail({ fb }) {
  const hasTrainer = fb.trainerTeachingQuality || fb.trainerClarity || fb.trainerDoubtHandling || fb.trainerEngagement;
  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-bg-surface-sunken tw-px-5 tw-py-4">
      <div>
        <h6 className={DETAIL_HEADING}><BookOpen size={14} /> Course Ratings</h6>
        <div className="tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-2 md:tw-grid-cols-2">
          <div className={DETAIL_ITEM}><span>Content Quality</span>{miniStars(fb.courseContent)}</div>
          <div className={DETAIL_ITEM}><span>Practice & Assignments</span>{miniStars(fb.coursePractice)}</div>
          <div className={DETAIL_ITEM}><span>Industry Relevance</span>{miniStars(fb.courseRelevance)}</div>
          <div className={DETAIL_ITEM}><span>Difficulty Level</span>{miniStars(fb.courseDifficulty)}</div>
        </div>
      </div>
      {hasTrainer && (
        <div>
          <h6 className={DETAIL_HEADING}><Users size={14} /> Trainer Ratings</h6>
          <div className="tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-2 md:tw-grid-cols-2">
            <div className={DETAIL_ITEM}><span>Teaching Quality</span>{miniStars(fb.trainerTeachingQuality)}</div>
            <div className={DETAIL_ITEM}><span>Clarity</span>{miniStars(fb.trainerClarity)}</div>
            <div className={DETAIL_ITEM}><span>Doubt Handling</span>{miniStars(fb.trainerDoubtHandling)}</div>
            <div className={DETAIL_ITEM}><span>Engagement</span>{miniStars(fb.trainerEngagement)}</div>
          </div>
        </div>
      )}
      {fb.suggestions && (
        <div>
          <h6 className={DETAIL_HEADING}><MessageSquare size={14} /> Suggestions</h6>
          <p className={SUGGESTIONS}>{fb.suggestions}</p>
        </div>
      )}
    </div>
  );
}

const AdminFeedbackPage = () => {
  const [loading, setLoading] = useState(true);
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [expandedId, setExpandedId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Filters
  const [courseFilter, setCourseFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);

  // Load filter options
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [cRes, bRes] = await Promise.all([
          apiClient.get('/courses/list', { params: { pageSize: 200 } }),
          apiClient.get('/batches/list', { params: { pageSize: 200 } }),
        ]);
        setCourses((cRes.data.courses || []).map((c) => ({ value: c._id, label: c.name })));
        setBatches((bRes.data.batches || []).map((b) => ({ value: b._id, label: b.name })));
      } catch { /* silent */ }
    };
    loadFilters();
  }, []);

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (courseFilter) params.course = courseFilter;
      if (batchFilter) params.batch = batchFilter;
      const { data } = await apiClient.get('/feedback/list', { params });
      setFeedbacks(data.feedbacks || []);
      setTotal(data.total || 0);
      setStats(data.stats || null);
    } catch {
      setToast({ show: true, message: 'Failed to load feedback.', type: 'error' });
    }
    setLoading(false);
  }, [page, pageSize, courseFilter, batchFilter]);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  useEffect(() => { setPage(1); }, [courseFilter, batchFilter]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <PageHeader
        icon={MessageSquare}
        title="Feedback & Ratings"
        description={`${total} feedback responses from students`}
      />

      {/* Averages Overview */}
      {stats && (
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
          <AppCard>
            <AppCardContent>
              <div className={STAT_HEADER}>
                <BookOpen size={16} />
                <span>Course Averages</span>
                <AppBadge variant="secondary">{stats.count} responses</AppBadge>
              </div>
              <div className="tw-grid tw-grid-cols-1 tw-gap-x-6 tw-gap-y-3 md:tw-grid-cols-2">
                <AvgStars value={stats.avgCourseContent} label="Content" />
                <AvgStars value={stats.avgCoursePractice} label="Practice" />
                <AvgStars value={stats.avgCourseRelevance} label="Relevance" />
                <AvgStars value={stats.avgCourseDifficulty} label="Difficulty" />
              </div>
            </AppCardContent>
          </AppCard>
          <AppCard>
            <AppCardContent>
              <div className={STAT_HEADER}>
                <Users size={16} />
                <span>Trainer Averages</span>
              </div>
              <div className="tw-grid tw-grid-cols-1 tw-gap-x-6 tw-gap-y-3 md:tw-grid-cols-2">
                <AvgStars value={stats.avgTrainerTeaching} label="Teaching" />
                <AvgStars value={stats.avgTrainerClarity} label="Clarity" />
                <AvgStars value={stats.avgTrainerDoubtHandling} label="Doubt Handling" />
                <AvgStars value={stats.avgTrainerEngagement} label="Engagement" />
              </div>
            </AppCardContent>
          </AppCard>
        </div>
      )}

      {/* Filters */}
      <div className="tw-flex tw-flex-wrap tw-gap-3">
        <select className={FILTER_SELECT} value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
          <option value="">All Courses</option>
          {courses.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select className={FILTER_SELECT} value={batchFilter} onChange={(e) => setBatchFilter(e.target.value)}>
          <option value="">All Batches</option>
          {batches.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
      </div>

      {/* Table */}
      {/* No spinner: Common/DataTable draws a real <tr>/<td> Shimmer when it is
          told it is loading, and the empty state is guarded so it cannot flash
          "No feedback yet" before the first response. Phase 6.5. */}
      {!loading && feedbacks.length === 0 && (
        <EmptyState
          icon={<BarChart3 />}
          title="No feedback yet"
          description="Feedback submitted by students will appear here."
        />
      )}

      {(loading || feedbacks.length > 0) && (
        <DataTable
          data={feedbacks}
          loading={loading}
          hoverable
          onRowClick={(fb) => setExpandedId((prev) => (prev === fb._id ? null : fb._id))}
          columns={[
            {
              key: 'student',
              label: 'Student',
              render: (fb) => (
                <>
                  <div className="tw-text-sm tw-font-semibold">{fb.student?.name || '—'}</div>
                  <div className="tw-text-xs tw-text-content-muted">{fb.student?.email || fb.student?.mobile || ''}</div>
                </>
              ),
            },
            { key: 'batch', label: 'Batch', render: (fb) => fb.batch?.name || '—' },
            { key: 'course', label: 'Course', render: (fb) => fb.course?.name || '—' },
            { key: 'trainer', label: 'Trainer', render: (fb) => fb.instructor?.name || '—' },
            {
              key: 'courseAvg',
              label: 'Course Avg',
              align: 'center',
              render: (fb) => <AppBadge variant="secondary">{courseAverage(fb)}</AppBadge>,
            },
            {
              key: 'trainerAvg',
              label: 'Trainer Avg',
              align: 'center',
              render: (fb) => {
                const t = trainerAverage(fb);
                return t ? <AppBadge variant="outline">{t}</AppBadge> : <span className="tw-text-[0.8125rem] tw-text-content-subtle">—</span>;
              },
            },
            {
              key: 'date',
              label: 'Date',
              className: 'tw-whitespace-nowrap tw-text-[0.8125rem] tw-text-content-muted',
              render: (fb) => new Date(fb.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }),
            },
            {
              key: 'expand',
              label: '',
              align: 'center',
              width: 50,
              render: (fb) => (expandedId === fb._id ? <ChevronUp size={16} /> : <ChevronDown size={16} />),
            },
          ]}
          renderExpandedRow={(fb) =>
            expandedId === fb._id ? (
              <tr>
                <td colSpan={8} className="!tw-border-t-0 !tw-p-0">
                  <FeedbackDetail fb={fb} />
                </td>
              </tr>
            ) : null
          }
          pagination={{
            page,
            pageSize,
            total,
            onPageChange: setPage,
            onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
            pageSizeOptions: [20, 50, 100],
          }}
        />
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default AdminFeedbackPage;
